// PO ↔ Vendor Invoice reconciliation API (REC). Backend: src/services/poInvoiceReconciliation.
import { get, postJson, patch, deleteRequest } from './apiClient';
import { destinationErrorMessage } from './vendorInvoiceDestinationApi';

// 'unit_mismatch' — the quantities are in different units, so no short/over
// verdict is possible. 6 cases against an order for 6 lbs used to read 'match'.
// 'data_missing' — the ordered qty or a billed qty was unreadable (not stated
// as 0); comparing the 0-for-arithmetic fallback on both sides used to read
// 'match' too, the same shape as 'unit_mismatch'.
// 'removed' — D81: a line removed from the order after the PO was sent (0 on
// the amended PO) that nobody billed: nothing is owed, so not 'missing'.
export type ReconLineStatus = 'match' | 'short' | 'over' | 'missing' | 'unit_mismatch' | 'data_missing' | 'removed';
// 'currency_mismatch' — ordered and billed in different currencies, so no
// price verdict is possible. TT$100 billed as US$100 used to read 'match'
// (USC-BUG-0008), the same shape as 'unit_mismatch' above.
// 'data_missing' — the ordered unit price or a billed price was unreadable.
export type ReconPriceStatus = 'match' | 'increase' | 'decrease' | 'currency_mismatch' | 'data_missing';
export type ReconSeverity = 'info' | 'warn' | 'critical';

export interface ReconMatchedInvoice {
  vendorInvoiceId: string;
  invoiceNumber: string;
  /** Position of this line within its invoice — what a manual pairing pins. */
  invoiceLineIndex: number;
  rawName: string;
  qty: number;
  uom: string;
  unitPrice: number;
  lineTotal: number;
  /** The currency THIS invoice billed in — two invoices on one PO line can differ. */
  currency?: string;
  /** 0..1. Below 0.9 the pairing was made on a partial name — worth a look. */
  matchScore: number;
  matchSource: 'auto' | 'manual';
}

/** Billed, but answering to no line on this PO. */
export interface ReconUnmatchedInvoiceLine {
  vendorInvoiceId: string;
  invoiceNumber: string;
  invoiceLineIndex: number;
  rawName: string;
  qty: number;
  uom: string;
  unitPrice: number;
  lineTotal: number;
  /** True when a person said so, rather than the matcher failing to find one. */
  excludedByHand: boolean;
  alerts: ReconAlert[];
}

export interface ReconAlert {
  type: string;
  severity: ReconSeverity;
  message: string;
}

export interface ReconLine {
  /** The line's position in the PO's items (removed lines counted): what a pin names. */
  poLineIndex: number;
  itemName: string;
  /** Off the order after the PO was sent (D81): never offered to pin a bill line to (D171.5). */
  removed?: boolean;
  orderedQty: number;
  orderedUom: string;
  orderedUnitPrice: number;
  orderedTotal: number;
  billedQty: number;
  billedUnitPrice: number;
  billedTotal: number;
  /**
   * What the billed figures are denominated in — NOT necessarily the PO's
   * currency. Blank when the matched invoice lines disagree with each other.
   * The panel labels billed money with this rather than with the PO's, which
   * is how a US$ figure came to be printed under a TT$ heading.
   */
  billedCurrency?: string;
  status: ReconLineStatus;
  priceDeltaPct: number;
  priceDeltaAmount: number;
  priceStatus: ReconPriceStatus;
  matchedInvoices: ReconMatchedInvoice[];
  alerts: ReconAlert[];
}

export interface ReconReport {
  po: {
    _id: string;
    poNumber: string;
    vendorName: string;
    totalPrice: number;
    /** The PO's own currency; the panel labels the ordered money with it (null: no label). */
    currency: string | null;
    /**
     * D82: 'before_vat' = each line's ordered and billed money is before VAT,
     * while `matchedInvoices[]` figures are as the invoice printed them.
     * null = a PO stored before D82, compared as printed.
     */
    priceBasis: 'before_vat' | null;
  };
  lines: ReconLine[];
  unmatchedInvoiceLines: ReconUnmatchedInvoiceLine[];
  /** D164: every invoice attached to the PO, whether or not any of its lines paired with one of the PO's. */
  attachedInvoices: AttachedInvoice[];
  totals: {
    orderedTotal: number;
    billedTotal: number;
    linesShort: number;
    linesOver: number;
    linesMissing: number;
    linesMatch: number;
    linesUnitMismatch: number;
    linesDataMissing: number;
    linesNotOrdered: number;
    priceAlerts: number;
    allMatch: boolean;
  };
}

/**
 * An invoice attached to the PO, as the panel lists it. `costApplied`: confirmed to Inventory, the only state in which its
 * prices cost a client invoice; false = attached and reconciled but not costing anything yet. Missing is null, never a guess.
 * `matchSource`: how the bill came onto the PO ('auto': it attached itself, D171.3), not the line scorer's source.
 */
export interface AttachedInvoice {
  vendorInvoiceId: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  total: number | null;
  currency: string | null;
  status: string | null;
  costApplied: boolean;
  matchSource?: 'auto' | 'manual' | '';
}

export async function getPoReconciliation(poId: string): Promise<ReconReport> {
  const res = await get<{ success: boolean; data: ReconReport }>(
    `/reconciliation/purchase-orders/${poId}/reconciliation`,
  );
  return res.data;
}

/**
 * D157.2 / D157.2b — one vendor invoice offered by a picker (a PO's Attach invoice, a client
 * invoice's Import Vendor Bill; server: poInvoiceReconciliation.service `candidateRow`). Missing is
 * null, never a guess: no printed number (show the file, as a file), no readable total, an assumed currency.
 */
export interface CandidateInvoice {
  _id: string;
  invoiceNumber: string | null;
  fileName: string | null;
  /** As printed; format it with utils/dateFormat. */
  invoiceDate: string | null;
  /** The registered vendor name, else the letterhead. */
  vendorName: string | null;
  total: number | null;
  currency: string | null;
  vessels: string[];
  /** Processing status of the document. */
  status: string | null;
  /** The picker's match rule pairs it (the PO: REC-2/B6; a client invoice: its order's bills). */
  matches: boolean;
}

/** Matches first, then the rest of the vendor's invoices; never an expense or one already attached. */
export async function listCandidateInvoices(poId: string): Promise<CandidateInvoice[]> {
  const res = await get<{ success: boolean; data: CandidateInvoice[] }>(
    `/reconciliation/purchase-orders/${poId}/candidate-invoices`,
  );
  return res.data;
}

/**
 * D171.8 — a client-invoice line whose cost an attach, detach, pin or unpin moved (server: clientInvoice.service
 * `recostPurchaseOrderLines`). `before` / `after`: its exact unit cost in the invoice's `currency` (null: none).
 */
export interface RecostedClientInvoiceLine {
  invoiceId: string;
  invoiceNumber: string | null;
  currency: string | null;
  lineNumber: number;
  itemName: string;
  before: number | null;
  after: number | null;
}

type LinkChangeResponse = { success: boolean; data: unknown; meta?: { recostedLines?: RecostedClientInvoiceLine[] } };
// Each of the four writes below answers the lines it re-costed; none named is none moved.
const recostedLinesOf = (res: LinkChangeResponse) => res.meta?.recostedLines ?? [];

export async function attachPoToInvoice(vendorInvoiceId: string, poId: string, poNumber: string) {
  return recostedLinesOf(await postJson<LinkChangeResponse>(
    `/reconciliation/vendor-invoices/${vendorInvoiceId}/attach-po`,
    { poId, poNumber },
  ));
}

export async function detachPoFromInvoice(vendorInvoiceId: string, poId: string) {
  return recostedLinesOf(await deleteRequest<LinkChangeResponse>(
    `/reconciliation/vendor-invoices/${vendorInvoiceId}/attach-po/${poId}`,
  ));
}

/**
 * D171.5 — pin a billed line to a line of ONE PO ("Pair with"), overriding the name matcher on that PO.
 * `poLineNumber` is the PO line's `poLineIndex` (its position), never its printed line number.
 * `null` says the billed line answers to nothing on this PO (no screen sends it).
 */
export async function setInvoiceLineMapping(
  vendorInvoiceId: string,
  poId: string,
  invoiceLineIndex: number,
  poLineNumber: number | null,
) {
  return recostedLinesOf(await patch<LinkChangeResponse>(
    `/reconciliation/vendor-invoices/${vendorInvoiceId}/line-mapping`,
    { poId, invoiceLineIndex, poLineNumber },
  ));
}

/** D171.5 — "Unpair": the pin goes and the matcher pairs that bill line on this PO again. */
export async function clearInvoiceLineMapping(vendorInvoiceId: string, poId: string, invoiceLineIndex: number) {
  return recostedLinesOf(await deleteRequest<LinkChangeResponse>(
    `/reconciliation/vendor-invoices/${vendorInvoiceId}/line-mapping/${poId}/${invoiceLineIndex}`,
  ));
}

// Keyed on the server's `code`, never its prose (server: poInvoiceReconciliation controller and service).
const PAIRING_MESSAGES: Record<string, string> = {
  RECON_PIN_PO_NOT_ATTACHED: 'This invoice is no longer attached to this PO. Reopen the panel to see it as it is now.',
  RECON_PIN_INVOICE_LINE_INVALID: 'That line is no longer on the invoice. Reopen the panel to see it as it is now.',
  RECON_PIN_PO_LINE_INVALID: 'That PO line is no longer on the order. Pick another line.',
  VENDOR_PO_NOT_FOUND: 'This purchase order no longer exists.',
  VENDOR_PO_ID_REQUIRED: 'No purchase order was named for this invoice. Reload the page and try again.',
};

/** Copy for a refused panel action (load, Attach, Remove, Pair with, Unpair); a destination refusal (an expense) reads as it does everywhere. */
export function pairingErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { code?: unknown } | null)?.code;
  return (typeof code === 'string' && PAIRING_MESSAGES[code]) || destinationErrorMessage(err, fallback);
}
